// src/lib/rbac.ts
import { ROUTE, type Route } from "./paths";

/* --- типы --- */
export type RoleName = "owner" | "superadmin" | "admin" | "door" | "guest";
export type Role = RoleName | null;

export type Capability =
  | "scan_qr"
  | "view_stats"
  | "manage_events"
  | "manage_prices"
  | "manage_finance"
  | "messages"
  | "manage_staff"
  | "dangerous_ops";

/* --- нормализация --- */
export function normalizeRole(r?: string | Role | null): Role {
  const v = String(r ?? "").trim().toLowerCase();
  if (v === "staff") return "door"; // старый алиас
  return (["owner", "superadmin", "admin", "door", "guest"] as const).includes(v as RoleName)
    ? (v as RoleName)
    : null;
}

/* --- политика --- */
export const POLICY: Record<Capability, readonly RoleName[]> = {
  scan_qr:        ["door", "admin", "superadmin", "owner"],
  view_stats:     ["admin", "superadmin", "owner"],
  manage_events:  ["admin", "superadmin", "owner"],
  manage_prices:  ["admin", "superadmin", "owner"],
  messages:       ["admin", "superadmin", "owner"],
  manage_finance: ["superadmin", "owner"],
  manage_staff:   ["superadmin", "owner"],
  dangerous_ops:  ["owner"],
};

/* — вспомогалки — */
function hasCap(r: Role, cap: Capability): boolean {
  const rn = normalizeRole(r);
  if (!rn) return false;
  // TS-ок: rn уже RoleName
  return POLICY[cap].includes(rn);
}

/* --- публичное API --- */
export function can(role: Role, cap: Capability): boolean {
  return hasCap(role, cap);
}

/* --- маршруты --- */
export function homeRouteFor(role: Role): Route {
  const r = normalizeRole(role);
  if (r === "owner" || r === "superadmin" || r === "admin") return ROUTE.super();
  if (r === "door") return ROUTE.door();
  return ROUTE.guest();
}

export function isRouteAllowed(route: Route, role: Role): boolean {
  // /door/*
  if (ROUTE.isDoorHome(route) || ROUTE.isDoorScan(route) || ROUTE.isDoorToken(route)) {
    return hasCap(role, "scan_qr");
  }
  // /super
  if (ROUTE.isSuperHome(route)) {
    const r = normalizeRole(role);
    return r === "owner" || r === "superadmin" || r === "admin";
  }
  // корень и /guest — общедоступны
  if (route.name === "root" || ROUTE.isGuestHome(route)) return true;

  return false;
}

/** Если доступ запрещён — подсказывает, куда редиректить */
export function guardRoute(current: Route, role: Role): { redirect: Route | null } {
  return isRouteAllowed(current, role)
    ? { redirect: null }
    : { redirect: homeRouteFor(role) };
}